import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import AnimationSettingsSection from '../WidgetEditComponents/AnimationSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import ButtonWidgetStyle from '../WidgetEditComponents/ButtonWidgetStyle';
import CategoryTextStyleSection from '../WidgetEditComponents/CategoryTextStyleSection';
import ElementOrderControls from '../WidgetEditComponents/ElementOrderControls';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SimplisticQuoteEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ DEBUG: Prüfe ob Edit-Component geladen wird
    console.log('🚀 SimplisticQuoteEdit loaded:', {
        widgetType: widget?.type,
        widgetId: widget?.id,
        editDataKeys: Object.keys(editData || {}),
        categoryText: editData?.categoryText
    });
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    
    // Image Upload States
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [isUploadingContent, setIsUploadingContent] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 SimplisticQuote initialized - simplified layout system');
        }, 100); // Small delay to ensure editData is stable
        
        return () => clearTimeout(timer);
    }, []);

    // Text Input Configuration
    const textConfig = [
        {
            fieldName: 'categoryText',
            label: 'Category Text',
            type: 'input',
            placeholder: 'About Us',
            fontField: 'categoryTextFont',
            fontType: 'small',
            effectField: 'categoryTextEffect',
            // ✅ MARGIN CONTROLS
            marginTopField: 'categoryTextMarginTop',
            marginBottomField: 'categoryTextMarginBottom'
        },
        {
            fieldName: 'richText',
            label: 'Header Text',
            type: 'textarea',
            placeholder: 'Enter your main header text here...',
            rows: 3,
            fontField: 'richTextFont',
            fontType: 'big',
            effectField: 'richTextEffect',
            // ✅ MARGIN CONTROLS
            marginTopField: 'richTextMarginTop',
            marginBottomField: 'richTextMarginBottom'
        },
        {
            fieldName: 'richTextHighlightWords',
            label: 'Header Text - Highlight Words',
            type: 'input',
            placeholder: 'beautiful, quotes (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'richTextHighlightFont',
            fontType: 'small', // Anderer Font-Typ als Header Text
            effectField: 'richTextHighlightEffect'
            // ✅ KEINE MARGINS für Highlight Words (ist Teil des Header Texts)
        },
        {
            fieldName: 'simpleText',
            label: 'Paragraph Text',
            type: 'textarea',
            placeholder: 'Enter your paragraph text here...',
            rows: 4,
            fontField: 'simpleTextFont',
            fontType: 'small',
            effectField: 'simpleTextEffect',
            // ✅ MARGIN CONTROLS
            marginTopField: 'simpleTextMarginTop',
            marginBottomField: 'simpleTextMarginBottom'
        }
    ];

    // Color Picker Configuration
    const colorConfig = [
        [
            {
                fieldName: 'categoryTextColor',
                label: 'Category Text',
                defaultValue: '#999999',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'richTextColor',
                label: 'Header Text',
                defaultValue: '#2c3e50',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'richTextHighlightColor',
                label: 'Header Highlight',
                defaultValue: '#D4A574',
                icon: '/img/text-fat.svg'
            }
        ],
        [
            {
                fieldName: 'simpleTextColor',
                label: 'Paragraph Text',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'categoryTextBackgroundColor',
                label: 'Category Background',
                defaultValue: 'transparent',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'categoryTextBorderColor',
                label: 'Category Border',
                defaultValue: '#cccccc',
                icon: '/img/element.svg'
            }
        ]
    ];

    // Font Settings Configuration
    const fontConfig = [
        {
            label: 'Category Text',
            sizeField: 'categoryTextSize',
            weightField: 'categoryTextWeight',
            defaultSize: '12',
            defaultWeight: '500'
        },
        {
            label: 'Header Text',
            sizeField: 'richTextSize',
            weightField: 'richTextWeight',
            defaultSize: '44',
            defaultWeight: '600'
        },
        {
            label: 'Header Text - Highlight Words',
            sizeField: 'richTextHighlightSize',
            weightField: 'richTextHighlightWeight',
            defaultSize: '44', // Gleich wie Header Text
            defaultWeight: '800'
        },
        {
            label: 'Paragraph Text',
            sizeField: 'simpleTextSize',
            weightField: 'simpleTextWeight',
            defaultSize: '14',
            defaultWeight: '400'
        }
    ];

    // ✅ ADVANCED SETTINGS CONFIGURATION - EXAKT WIE ANDERE WIDGETS
    const advancedConfig = [
        // Text Spacing Controls
        {
            fieldName: 'textGap',
            label: 'Text Elements Gap',
            type: 'slider',
            defaultValue: 25,
            min: 0,
            max: 80,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg',
            description: 'Space between text elements (Category, Header, Paragraph)'
        },
        {
            fieldName: 'textGapMobile',
            label: 'Mobile Text Elements Gap',
            type: 'slider',
            defaultValue: 20,
            min: 0,
            max: 60,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg',
            description: 'Space between text elements on mobile devices'
        },
        
        // Text Alignment
        {
            fieldName: 'textAlignment',
            label: 'Text Alignment',
            type: 'dropdown',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg'
        },
        {
            fieldName: 'textAlignmentMobile',
            label: 'Mobile Text Alignment',
            type: 'dropdown',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg'
        },
        
        {
            fieldName: 'richTextHighlightWeight',
            label: 'Header Highlight Weight',
            type: 'dropdown',
            options: [
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' }
            ],
            defaultValue: '800',
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'richTextHighlightMarginLeft',
            label: 'Header Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'richTextHighlightMarginRight',
            label: 'Header Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        
        // Content Image Settings
        {
            fieldName: 'imageWidth',
            label: 'Image Width',
            type: 'number',
            defaultValue: 300,
            min: 100,
            max: 500,
            step: 10,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'imageSpacing',
            label: 'Image Spacing',
            type: 'number',
            defaultValue: 20,
            min: 0,
            max: 50,
            step: 5,
            unit: 'px',
            icon: '/img/element.svg'
        },

        // Visibility Controls (Behavior)
        {
            fieldName: 'showCategoryText',
            label: 'Show Category Text',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showRichText',
            label: 'Show Header Text',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showSimpleText',
            label: 'Show Paragraph Text',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },
        {
            fieldName: 'showContentImage',
            label: 'Show Content Image',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/element.svg'
        },
        {
            fieldName: 'showBackgroundImage',
            label: 'Show Background Image',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'showButtonText',
            label: 'Show Button',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/button.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                
                {/* ✅ ELEMENT ORDER CONTROLS - Pfeil-basierte Reihenfolge */}
                <div className="element-order-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <i className="fa fa-sort"></i>
                            Element Order
                        </h3>
                        <p className="section-description">
                            🎯 <strong>Drag & Drop:</strong> Grab any element by the grip handle (⋮⋮) and drag it between other elements to reorder. 
                            <br />✨ <strong>Visual Guide:</strong> Blue insertion lines show exactly where the element will be placed.
                        </p>
                    </div>
                    <ElementOrderControls
                        elementOrder={editData.elementOrder || {
                            image: 1,
                            categoryText: 2,
                            richText: 3,
                            simpleText: 4,
                            button: 5
                        }}
                        onOrderChange={(newOrder) => {
                            const fakeEvent = {
                                target: { name: 'elementOrder', value: newOrder, type: 'object' }
                            };
                            handleInputChange(fakeEvent);
                        }}
                        editData={editData}
                    />
                </div>

                {/* ✅ BACKGROUND IMAGE CONTAINER - mit Opacity & Effects */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    showToggle={true}
                    toggleLabel="Show Background Image"
                    toggleFieldName="showBackgroundImage"
                    toggleChecked={editData.showBackgroundImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Background Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 80,
                            min: 10,
                            max: 100,
                            step: 5,
                            unit: '%',
                            description: 'Opacity of the background image'
                        },
                        {
                            type: 'slider',
                            label: 'Background Blur',
                            fieldName: 'backgroundImageBlur',
                            defaultValue: 0,
                            min: 0,
                            max: 10,
                            step: 1,
                            unit: 'px',
                            description: 'Blur effect for background image'
                        },
                        {
                            type: 'image-effect',
                            label: 'Background Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* ✅ CONTENT IMAGE CONTAINER */}
                <ImageContainer
                    label="Content Image"
                    fieldName="optionalImage"
                    currentImage={editData.optionalImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingContent}
                    setIsUploading={setIsUploadingContent}
                    showToggle={true}
                    toggleLabel="Show Content Image"
                    toggleFieldName="showContentImage"
                    toggleChecked={editData.showContentImage !== false}
                    onToggleChange={handleInputChange}
                    controls={[
                        {
                            type: 'slider',
                            label: 'Image Width (Desktop)',
                            fieldName: 'imageWidth',
                            defaultValue: 300,
                            min: 100,
                            max: 500,
                            step: 10,
                            unit: 'px',
                            description: 'Width of the content image on desktop'
                        },
                        {
                            type: 'slider',
                            label: 'Image Width (Mobile)',
                            fieldName: 'imageWidthMobile',
                            defaultValue: 200,
                            min: 80,
                            max: 350,
                            step: 10,
                            unit: 'px',
                            description: 'Width of the content image on mobile'
                        },
                        {
                            type: 'slider',
                            label: 'Image Margin (Desktop)',
                            fieldName: 'imageMargin',
                            defaultValue: 20,
                            min: 0,
                            max: 80,
                            step: 5,
                            unit: 'px',
                            description: 'Space between image and text on desktop',
                            icon: '📏'
                        },
                        {
                            type: 'slider',
                            label: 'Image Margin (Mobile)',
                            fieldName: 'imageMarginMobile',
                            defaultValue: 15,
                            min: 0,
                            max: 60,
                            step: 5,
                            unit: 'px',
                            description: 'Space between image and text on mobile',
                            icon: '📱'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* ✅ TEXT SETTINGS */}
                <TextSettingsSection
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={true}
                />

                {/* ✅ CATEGORY TEXT STYLING - Separate Komponente */}
                {editData.showCategoryText !== false && (
                    <CategoryTextStyleSection 
                        editData={editData}
                        handleInputChange={handleInputChange}
                    />
                )}

                {/* ✅ BUTTON STYLE SECTION - nur wenn Button aktiv */}
                {editData.showButtonText !== false && (
                    <ButtonWidgetStyle 
                        editData={editData}
                        handleInputChange={handleInputChange}
                        buttonConfig={{
                            textField: 'buttonText',
                            linkField: 'buttonLink'
                        }}
                    />
                )}

                {/* ✅ ANIMATION SETTINGS */}
                <AnimationSettingsSection 
                    editData={editData}
                    onUpdate={onUpdate}
                    showSpeedControls={true}
                    showDelayControls={true}
                    defaultExpanded={false}
                />

                {/* ✅ REVOLUTIONARY LAYOUT SETTINGS */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={[
                        {
                            section: 'Width Settings',
                            icon: 'fa-arrows-h',
                            properties: [
                                {
                                    id: 'maxWidth',
                                    label: 'Container Max Width',
                                    min: 300,
                                    max: 1400,
                                    step: 50,
                                    unit: 'px',
                                    icon: '↔️',
                                    description: 'Maximum width of the content container',
                                    defaultValue: 800,
                                    allowNone: true,
                                    toggleable: true
                                },
                                {
                                    id: 'enableTextMaxWidth',
                                    label: 'Enable Text Max Width',
                                    type: 'toggle',
                                    icon: '📐',
                                    description: 'Limit the maximum width of text content',
                                    defaultValue: false
                                },
                                {
                                    id: 'textMaxWidth',
                                    label: 'Text Max Width',
                                    min: 300,
                                    max: 1200,
                                    step: 50,
                                    unit: 'px',
                                    icon: '📏',
                                    description: 'Maximum width for text content',
                                    defaultValue: 600,
                                    showWhen: 'enableTextMaxWidth' // ✅ Nur zeigen wenn Toggle aktiv
                                }
                            ]
                        }
                    ]}
                />

                {/* ✅ ADVANCED SETTINGS */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* ✅ MORE SETTINGS (Colors & Fonts only) */}
                <MoreSettingsSection
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE SECTION */}
                <div className="save-template-section">
                    <div className="save-template-header-text">
                        <h4 className="save-template-title">Save as Template</h4>
                        <p className="save-template-description">
                            Save your current design as a reusable template
                        </p>
                    </div>
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                    >
                        <i className="fas fa-bookmark"></i>
                        Create Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default SimplisticQuoteEdit;